import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Rocket, LogOut, Search, Loader2, Mail, Users, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { adminCheck, adminUsers, adminDeleteUser, adminLogout } from "../lib/api";
import { AdminNav } from "./AdminContinueBuilding";

const fmtDate = (v) => {
  if (!v) return "—";
  try { return new Date(v).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
  catch { return "—"; }
};

export default function AdminEmail() {
  const navigate = useNavigate();
  const [authorized, setAuthorized] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(() => new Set());

  useEffect(() => {
    adminCheck()
      .then((r) => { if (!r.admin) { setAuthorized(false); navigate("/"); return; } setAuthorized(true); })
      .catch(() => { setAuthorized(false); navigate("/"); });
  }, [navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try { const d = await adminUsers(); setUsers((d.users || []).filter((u) => u.email)); }
    catch { setUsers([]); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { if (authorized) load(); }, [authorized, load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q));
  }, [users, search]);

  const allVisibleSelected = filtered.length > 0 && filtered.every((u) => selected.has(u.email));

  const toggle = (email) => {
    setSelected((prev) => { const n = new Set(prev); n.has(email) ? n.delete(email) : n.add(email); return n; });
  };
  const toggleAll = () => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (allVisibleSelected) filtered.forEach((u) => n.delete(u.email));
      else filtered.forEach((u) => n.add(u.email));
      return n;
    });
  };

  const openGmail = () => {
    const emails = [...selected];
    if (emails.length === 0) { toast.error("Please select at least one user."); return; }
    const url = `https://mail.google.com/mail/?authuser=launchbs247@gmail.com&view=cm&fs=1&bcc=${encodeURIComponent(emails.join(","))}`;
    window.open(url, "_blank", "noopener");
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Delete ${u.name || u.email}? This removes their account permanently.`)) return;
    setUsers((prev) => prev.filter((x) => x.user_id !== u.user_id));
    setSelected((prev) => { const n = new Set(prev); n.delete(u.email); return n; });
    try { await adminDeleteUser(u.user_id); toast.success("User deleted"); }
    catch { toast.error("Could not delete user"); load(); }
  };

  if (authorized === null) {
    return <div className="min-h-screen flex items-center justify-center bg-[#0B0B0B]"><Loader2 className="animate-spin text-[#D4AF37]" size={30} /></div>;
  }

  return (
    <div className="min-h-screen bg-[#0B0B0B] text-white" data-testid="admin-email-page">
      <header className="border-b border-white/10 bg-[#0B0B0B]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center"><Rocket className="text-[#D4AF37]" size={18} /></div>
              <div><p className="font-display font-bold leading-tight">Command Center</p><p className="text-xs text-[#71717A]">Email Users</p></div>
            </div>
            <button onClick={async () => { await adminLogout(); navigate("/"); }} className="inline-flex items-center gap-2 text-sm text-[#A1A1AA] hover:text-white transition-colors" data-testid="admin-logout-btn">
              <LogOut size={16} /> Sign out
            </button>
          </div>
          <AdminNav active="email" />
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
        <h1 className="font-display text-2xl font-extrabold">Email Users</h1>
        <p className="text-sm text-[#A1A1AA] mt-1">Select users and open Gmail with one click.</p>

        {/* Toolbar */}
        <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-3">
          <form onSubmit={(e) => e.preventDefault()} className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 flex-1 max-w-md">
            <Search size={16} className="text-[#71717A]" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or email..." className="bg-transparent text-sm outline-none flex-1 placeholder:text-[#71717A]" data-testid="admin-email-search" />
          </form>
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-2 text-sm text-[#A1A1AA]" data-testid="admin-email-selected-count">
              <Users size={15} className="text-[#D4AF37]" /> {selected.size} User{selected.size === 1 ? "" : "s"} Selected
            </span>
            <button onClick={openGmail} className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0B0B0B] rounded-xl px-5 py-2.5 text-sm font-bold hover:bg-[#c5a030] transition-colors disabled:opacity-50" data-testid="admin-email-open-gmail">
              <Mail size={16} /> Open Gmail
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="mt-5 rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
          {loading ? (
            <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-[#D4AF37]" size={26} /></div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center text-[#71717A] text-sm">No users found.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10 text-[#71717A] text-left">
                  <th className="px-4 py-3 w-12">
                    <input type="checkbox" checked={allVisibleSelected} onChange={toggleAll} className="w-4 h-4 accent-[#D4AF37] cursor-pointer" data-testid="admin-email-select-all" aria-label="Select all" />
                  </th>
                  <th className="px-4 py-3 font-semibold">Name</th>
                  <th className="px-4 py-3 font-semibold">Email</th>
                  <th className="px-4 py-3 font-semibold whitespace-nowrap">Joined</th>
                  <th className="px-4 py-3 w-12"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => {
                  const on = selected.has(u.email);
                  return (
                    <tr key={u.user_id || u.email} onClick={() => toggle(u.email)} className={`border-b border-white/5 cursor-pointer transition-colors ${on ? "bg-[#D4AF37]/10" : "hover:bg-white/5"}`} data-testid={`admin-email-row-${u.email}`}>
                      <td className="px-4 py-3">
                        <input type="checkbox" checked={on} onChange={() => toggle(u.email)} onClick={(e) => e.stopPropagation()} className="w-4 h-4 accent-[#D4AF37] cursor-pointer" data-testid={`admin-email-check-${u.email}`} />
                      </td>
                      <td className="px-4 py-3 font-medium text-white">{u.name || "—"}</td>
                      <td className="px-4 py-3 text-[#A1A1AA]">{u.email}</td>
                      <td className="px-4 py-3 text-[#71717A] whitespace-nowrap">{fmtDate(u.created_at)}</td>
                      <td className="px-4 py-3">
                        <button onClick={(e) => { e.stopPropagation(); deleteUser(u); }} className="text-[#71717A] hover:text-red-500 transition-colors" data-testid={`admin-email-delete-${u.email}`} title="Delete user" aria-label="Delete user">
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        <p className="text-xs text-[#71717A] mt-3">{filtered.length} user{filtered.length === 1 ? "" : "s"} · Sends from <span className="text-[#D4AF37] font-medium">launchbs247@gmail.com</span> · Emails open in Gmail using BCC so recipients can't see each other.</p>
      </main>
    </div>
  );
}
